import React, {
  useRef,
} from "react";
import "./card.css";
import type { ICard } from "../../api/cardsSlice";

interface ICardProps {
  card: ICard;
}

export const Card:
  React.FC<ICardProps> = ({
    card,
  }) => {
    const dialogRef =
      useRef<HTMLDialogElement>(
        null
      );

    const {
      id,
      arcana,
      court,
      number,
      name,
      nameEn,
      keywords,
      symbolism,
      symbolismInterpretation,
      archetype,
      psychology,
      upright,
      reversed,
      associations,
      combinations,
      readingTips,
      selfQuestions,
    } = card;

    const meta:
      string[] = [];

    if (
      arcana === "major"
    ) {
      meta.push(
        "Старший аркан",
        `№ ${number}`
      );
    } else {
      if (
        court &&
        court !== "simple" &&
        court !== "pip"
      ) {
        meta.push(
          `Фигура: ${court}`
        );
      }

      meta.push(
        `№ ${number}`
      );
    }

    const openDialog = () => {
      dialogRef.current
        ?.showModal();
    };

    const closeDialog = () => {
      dialogRef.current
        ?.close();
    };

    const handleDialogClick = (
      event:
        React.MouseEvent<HTMLDialogElement>
    ) => {
      if (
        event.target ===
        event.currentTarget
      ) {
        closeDialog();
      }
    };

    const renderList = (
      label: string,
      items?: string[]
    ) =>
      items &&
      items.length >
        0 ? (
        <section className="card-dialog__section">
          <h3 className="card-dialog__label">
            {label}
          </h3>

          <ul className="card-dialog__list">
            {items.map(
              (
                item,
                index
              ) => (
                <li
                  key={`${label}-${index}`}
                >
                  {item}
                </li>
              )
            )}
          </ul>
        </section>
      ) : null;

    const renderText = (
      label: string,
      value?: string
    ) =>
      value ? (
        <section className="card-dialog__section">
          <h3 className="card-dialog__label">
            {label}
          </h3>

          <p className="card-dialog__text">
            {value}
          </p>
        </section>
      ) : null;

    return (
      <>
        <article
          className="card-entry"
          data-card-id={id}
        >
          <header className="card-entry__header">
            <div className="card-entry__meta">
              {meta.map(
                (item) => (
                  <span
                    key={
                      item
                    }
                  >
                    {item}
                  </span>
                )
              )}
            </div>

            <h3 className="card-entry__title">
              {name}
            </h3>

            {nameEn ? (
              <div className="card-entry__name-en">
                {nameEn}
              </div>
            ) : null}
          </header>

          {keywords.length >
          0 ? (
            <div className="card-entry__keywords">
              {keywords.map(
                (
                  keyword,
                  index
                ) => (
                  <span
                    key={`${keyword}-${index}`}
                  >
                    {
                      keyword
                    }
                  </span>
                )
              )}
            </div>
          ) : null}

          <div className="card-entry__footer">
            <button
              className="card-entry__open"
              type="button"
              onClick={
                openDialog
              }
            >
              <span
                aria-hidden="true"
              >
                ✦
              </span>

              Полное описание
            </button>
          </div>
        </article>

        <dialog
          ref={dialogRef}
          className="card-dialog"
          aria-labelledby={
            `card-dialog-title-${id}`
          }
          onClick={
            handleDialogClick
          }
        >
          <div className="card-dialog__panel">
            <header className="card-dialog__header">
              <div className="card-dialog__heading">
                <div className="card-dialog__meta">
                  {meta.map(
                    (item) => (
                      <span
                        key={
                          item
                        }
                      >
                        {item}
                      </span>
                    )
                  )}
                </div>

                <h2
                  className="card-dialog__title"
                  id={`card-dialog-title-${id}`}
                >
                  {name}
                </h2>

                {nameEn ? (
                  <div className="card-dialog__name-en">
                    {nameEn}
                  </div>
                ) : null}
              </div>

              <button
                className="card-dialog__close"
                type="button"
                aria-label="Закрыть описание карты"
                onClick={
                  closeDialog
                }
              >
                ×
              </button>
            </header>

            {keywords.length >
            0 ? (
              <div className="card-dialog__keywords">
                {keywords.map(
                  (
                    keyword,
                    index
                  ) => (
                    <span
                      key={`${keyword}-${index}`}
                    >
                      {
                        keyword
                      }
                    </span>
                  )
                )}
              </div>
            ) : null}

            <div className="card-dialog__body">
              {renderList(
                "Символика",
                symbolism
              )}

              {renderText(
                "Толкование символики",
                symbolismInterpretation
              )}

              {renderText(
                "Архетип",
                archetype
              )}

              {renderText(
                "Психология",
                psychology
              )}

              <section className="card-dialog__section">
                <h3 className="card-dialog__label">
                  Прямое положение
                </h3>

                <div className="card-dialog__subsections">
                  {renderText(
                    "Личность",
                    upright.personality
                  )}

                  {renderText(
                    "Отношения",
                    upright.relationships
                  )}

                  {renderText(
                    "Работа",
                    upright.work
                  )}

                  {renderText(
                    "Духовность",
                    upright.spirituality
                  )}

                  {renderList(
                    "Примеры",
                    upright.examples
                  )}
                </div>
              </section>

              <section className="card-dialog__section">
                <h3 className="card-dialog__label">
                  Перевёрнутое положение
                </h3>

                <div className="card-dialog__subsections">
                  {renderText(
                    "Значение",
                    reversed.meaning
                  )}

                  {renderText(
                    "Нюансы",
                    reversed.nuances
                  )}
                </div>
              </section>

              {renderList(
                "Ассоциации",
                associations
              )}

              {renderList(
                "Сочетания",
                combinations
              )}

              {renderList(
                "Советы чтения",
                readingTips
              )}

              {renderList(
                "Вопросы к себе",
                selfQuestions
              )}
            </div>
          </div>
        </dialog>
      </>
    );
  };


